import {
  MaterialTransferido,
} from "@/shared/interfaces";
import { EquiposUtilizadosOTTableType } from "../../equipos";
import { EstadosActivacionInstallOTEnumChoice } from "@/shared";

const ZERO_QUANTITY = "0.00";
const CODIGOS_FIBRA_PLANA = ["MFO.015.0082", "MFO.015.0046"];
const CATEGORIAS_PUNTOS_RED = ["PUNTOS DE RED", "PUNTOS_RED", "UTP"];

export type ResultadoValidacion =
  | { valido: true }
  | { valido: false; mensaje: string };

interface ValidarOrdenTrabajoParams {
  data: {
    fecha_hora_inicio_instalacion?: unknown;
    ont_a_utilizar?: unknown;
    equipos_utilizados?: unknown;
    puntos_red_instalados?: unknown;
  };
  esBorrador: boolean;
  ordentrabajo: {
    estado_activacion?: string;
    equipos_utilizados?: MaterialTransferido[];
  };
  equiposUtilizados: EquiposUtilizadosOTTableType[];
  materialesFiltrados: MaterialTransferido[];
  productosAdicionales: { series?: string[] }[];
}

export function validarOrdenTrabajo({
  data,
  esBorrador,
  ordentrabajo,
  equiposUtilizados,
  materialesFiltrados,
  productosAdicionales,
}: ValidarOrdenTrabajoParams): ResultadoValidacion {
  // Regla que aplica SIEMPRE, incluso para borradores.
  if (!data.fecha_hora_inicio_instalacion) {
    return { valido: false, mensaje: "Debe iniciar la instalación para continuar." };
  }

  // El borrador se salta el resto de las reglas de negocio.
  if (esBorrador) {
    return { valido: true };
  }

  if (!data.ont_a_utilizar) {
    return { valido: false, mensaje: "Debe seleccionar un ONT" };
  }

  if (ordentrabajo?.estado_activacion !== EstadosActivacionInstallOTEnumChoice.GESTIONADA) {
    return {
      valido: false,
      mensaje: "La orden de trabajo aún no ha sido gestionada por ACTIVACIONES",
    };
  }

  if (!equiposUtilizados.length && !data.equipos_utilizados) {
    return { valido: false, mensaje: "No se han agregado EQUIPOS a la orden de trabajo" };
  }

  const resultadoEquipos = validarSeriesYCantidadesEquipos(
    ordentrabajo?.equipos_utilizados,
    equiposUtilizados,
  );
  if (!resultadoEquipos.valido) return resultadoEquipos;

  if (materialesFiltrados.length === 0) {
    return { valido: false, mensaje: "No se han agregado materiales de instalación." };
  }

  if (Number(data.puntos_red_instalados || 0) >= 1) {
    const tieneMaterialPuntoRed = materialesFiltrados.some(esMaterialPuntoRed);
    if (!tieneMaterialPuntoRed) {
      return {
        valido: false,
        mensaje: "Debe seleccionar al menos un material de la categoría Puntos de red.",
      };
    }
  }

  const tieneFibraPlana = materialesFiltrados.some((item) =>
    CODIGOS_FIBRA_PLANA.includes(item.codigo!),
  );
  if (!tieneFibraPlana) {
    return {
      valido: false,
      mensaje: "No se ha agregado FIBRA PLANA a los materiales de instalación.",
    };
  }

  const resultadoSeries = validarSeriesMateriales(materialesFiltrados);
  if (!resultadoSeries.valido) return resultadoSeries;

  const hayProdAdicionalesInvalidos = productosAdicionales.some(
    (obj) => !obj.series || (Array.isArray(obj.series) && obj.series.length === 0),
  );
  if (hayProdAdicionalesInvalidos) {
    return {
      valido: false,
      mensaje: "Algunos productos adicionales tienen series pendientes por cargar",
    };
  }

  return { valido: true };
}

function normalizarTexto(value?: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function esMaterialPuntoRed(item: MaterialTransferido): boolean {
  const producto = item.producto_data;
  if (producto?.es_utp) return true;

  const categorias = [
    producto?.categoria_producto,
    producto?.categoria_producto_data?.codigo,
    producto?.categoria_producto_data?.nombre,
    producto?.subcategoria_producto,
    producto?.subcategoria_producto_data?.codigo,
    producto?.subcategoria_producto_data?.nombre,
  ].map(normalizarTexto);

  return categorias.some((categoria) => CATEGORIAS_PUNTOS_RED.includes(categoria));
}

function validarSeriesYCantidadesEquipos(
  equiposAlmacenados: MaterialTransferido[] | undefined,
  equiposUtilizados: EquiposUtilizadosOTTableType[],
): ResultadoValidacion {
  const itemsAValidar =
    equiposAlmacenados && equiposAlmacenados.length > 0
      ? equiposAlmacenados
      : equiposUtilizados.map((item) => ({
          cantidad: (item?.cantidad as number)?.toFixed(2),
          contiene_serie: item?.contiene_serie,
          series: item?.selected_series || [],
        }));

  const hayItemsConCantidadCero = itemsAValidar.some(
    (item) => item?.cantidad === ZERO_QUANTITY,
  );
  if (hayItemsConCantidadCero) {
    return { valido: false, mensaje: "Hay EQUIPOS sin cantidad" };
  }

  const hayItemsSinSeries = itemsAValidar.some(
    (item) => item?.contiene_serie && !item?.series?.length,
  );
  if (hayItemsSinSeries) {
    return { valido: false, mensaje: "Hay EQUIPOS sin series asignadas" };
  }

  return { valido: true };
}

function validarSeriesMateriales(
  materialesFiltrados: MaterialTransferido[],
): ResultadoValidacion {
  for (const el of materialesFiltrados) {
    if (!el.contiene_serie) continue;

    const seriesLen = el.series?.length ?? 0;
    const cantidad = el.cantidad ?? 0;
    const codigo = el.producto_data?.codigo ?? "(sin código)";

    if (seriesLen === 0) {
      return {
        valido: false,
        mensaje: `El producto con el código ${codigo} tiene series pendientes por cargar`,
      };
    }
    if (seriesLen !== parseInt(cantidad.toString())) {
      return {
        valido: false,
        mensaje: `El producto con el código ${codigo} tiene una discrepancia entre la cantidad ingresada y las series cargadas. Por favor, revisa la información y vuelve a intentarlo.`,
      };
    }
  }

  return { valido: true };
}
